import React, { Fragment, useState, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';
import IconToast from '../NewToast';
import { handleError } from '../../utils/openai';
import { openaiGenerateWithTextPolling } from '../../store/openai/api';
import RecordDialog from '../RecordDialog/RecordDialog';
import LoadingBackdrop from '../LoadingBackdrop/LoadingBackdrop';
import { speechToText } from '../../store/speech/api';
import { injectIntl } from 'react-intl';
import NativeOrWeb from '../../utils/native';
import { tracking, voiceRecordTrackingEvent, gptTrackingEvent } from '../../utils/tracking';
import { trackDirect } from '../../store/analytics/api';

const RecordMeetingRemarkButton = forwardRef((props, ref) => {
  const { intl, ButtonProps, onResult, isRecruit } = props;

  const [initializing, setInitializing] = useState(false);
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    openDialog: openDialog
  }));

  const openDialog = () => {
    setRecordingDialogOpen(true);
  };

  const startRecording = async () => {
    try {
      tracking(voiceRecordTrackingEvent, {
        feature: 'meeting_note',
        status: 'start'
      });
      trackDirect('useAiFeature');
      trackDirect('convertVoiceToMeetingNote');
      tracking('AI: Convert Voice to Meeting Note');

      setRecording(true);
      setInitializing(true);
      await NativeOrWeb.startRecordingVoice();
    } catch (error) {
      tracking(voiceRecordTrackingEvent, {
        feature: 'meeting_note',
        status: 'error',
        ...error
      });
      setRecordingDialogOpen(false);
      setRecording(false);
      if (error.message === 'FAILED_TO_RECORD' || error.message === 'User did not allow voice recording') {
        NativeOrWeb.openRecorderPermissionDialog();
      } else {
        toast.info(IconToast('error', intl.formatMessage({ id: 'error-msg' })), { className: 'new-toast' });
      }
    } finally {
      setInitializing(false);
    }
  };

  const cancelRecording = () => {
    setRecordingDialogOpen(false);
    setRecording(false);
    if (recording) {
      NativeOrWeb.stopRecordingVoice()
        .then()
        .catch(console.log);
    }
    tracking(voiceRecordTrackingEvent, {
      feature: 'meeting_note',
      status: 'cancel'
    });
  };

  const stopRecording = async () => {
    try {
      setRecordingDialogOpen(false);
      setLoading(true);

      tracking(voiceRecordTrackingEvent, {
        feature: 'meeting_note',
        status: 'complete'
      });
      const recordingData = await NativeOrWeb.stopRecordingVoice();
      console.log('RecordMeetingRemarkButton :: recordingData:: \n', recordingData);
      const { mimeType, msDuration, recordDataBase64 } = recordingData?.value || {};
      const speechToTextResult = await speechToText({ mimeType, msDuration, recordDataBase64 });
      const { text: speechText } = speechToTextResult?.result || {};
      const response = await openaiGenerateWithTextPolling(
        isRecruit ? 'voice_create_recruitment_note' : 'voice_create_meeting_note',
        { language: intl.locale, speechText: speechText },
        []
      );

      const { result, error } = response || {};

      if (error) {
        tracking(gptTrackingEvent, {
          feature: 'meeting_note',
          status: 'error',
          ...error
        });
        throw new Error(error);
      }

      tracking(gptTrackingEvent, {
        feature: 'meeting_note',
        status: 'success'
      });

      onResult(result);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
      setRecording(false);
    }
  };

  return (
    <Fragment>
      <RecordDialog
        title={intl.formatMessage({ id: isRecruit ? 'ai-voice-to-recruit-notes' : 'ai-voice-to-notes' })}
        content={intl.formatMessage({
          id: isRecruit ? 'ai-voice-to-recruit-notes-content' : 'ai-voice-to-notes-content'
        })}
        timeLimit={180}
        open={recordingDialogOpen}
        initializing={initializing}
        recording={recording}
        onClose={cancelRecording}
        onStartRecording={startRecording}
        onDoneRecording={stopRecording}
      />
      <LoadingBackdrop open={loading} maxLoadingTime={30} />
      <Button
        {...ButtonProps}
        variant="contained"
        sx={{
          padding: '2px 10px !important',
          fontSize: '12px !important',
          marginLeft: '4px !important',
          minWidth: 'unset !important',
          background: isRecruit 
            ? 'linear-gradient(135deg, #FF9C00, #ff7300) !important' 
            : 'linear-gradient(135deg, #3966f8, #665eff) !important',
          color: '#ffffff !important',
          boxShadow: isRecruit 
            ? '0 3px 8px rgba(255, 156, 0, 0.4) !important' 
            : '0 3px 8px rgba(57, 102, 248, 0.4) !important',
          border: 'none !important',
          textTransform: 'none !important',
          fontWeight: '600 !important',
          display: 'inline-flex !important',
          alignItems: 'center !important',
          transition: 'all 0.2s ease !important',
          '&:hover': {
            background: isRecruit 
              ? 'linear-gradient(135deg, #ffaa22, #ff8811) !important' 
              : 'linear-gradient(135deg, #4d76f9, #7770ff) !important',
            boxShadow: isRecruit 
              ? '0 4px 12px rgba(255, 156, 0, 0.5) !important' 
              : '0 4px 12px rgba(57, 102, 248, 0.5) !important',
            transform: 'translateY(-0.5px)'
          },
          '&:active': {
            transform: 'translateY(0.5px)'
          },
          ...ButtonProps?.sx
        }}
        className={`voice ${ButtonProps?.className || ''}`}
        onClick={openDialog}
      >
        <img
          style={{
            width: '14px',
            height: '14px',
            paddingRight: '2px',
            filter: 'brightness(0) invert(1)'
          }}
          src="/img/icons/ai_bg_free.svg"
          alt=""
        />
        {intl.formatMessage({ id: isRecruit ? 'recruit' : 'voice' })}
      </Button>
    </Fragment>
  );
});

export default injectIntl(RecordMeetingRemarkButton, { withRef: true });
